import { Dialog } from "./Dialog";

const calendar: HTMLElement = document.querySelector(".calendar");
const eventsList: HTMLElement = document.querySelector(".calendar-events-list");

if (calendar && eventsList) {
    const calendarPrevMonthButton: HTMLButtonElement = calendar.querySelector(".change-month-button--prev");
    const calendarNextMonthButton: HTMLButtonElement = calendar.querySelector(".change-month-button--next");
    const prevDayButton: HTMLButtonElement = calendar.querySelector(".change-day-button--prev");
    const nextDayButton: HTMLButtonElement = calendar.querySelector(".change-day-button--next");
    const calendarTodayButton = calendar.querySelector(".today-button");
    const calendarMonthsHeader: NodeListOf<HTMLElement> = calendar.querySelectorAll(".active-month-title .month");
    const calendarMonths: NodeListOf<HTMLElement> = calendar.querySelectorAll(".calendar-month");
    const calendarTodayView = calendar.querySelector(".calendar-month--today");
    const calendarDayTodayView: HTMLElement = calendar.querySelector(".calendar-day--today");
    const calendarTodayViewIndex: number = parseInt(calendarTodayView.getAttribute("data-index-month"));
    const calendarEvents: NodeListOf<HTMLElement> = calendar.querySelectorAll(".event");
    const calendarDays: NodeListOf<HTMLElement> = calendar.querySelectorAll(".calendar-month .calendar-day");
    const calendarDayMobileWrapper: HTMLButtonElement = calendar.querySelector(".calendar-day-mobile");
    const totalMonths: number = calendarMonths.length; // amount of displayed months
    /* activeCalendar */
    let activeCalendar = calendarTodayView;
    let activeCalendarMonthIndex: number = parseInt(calendarTodayView.getAttribute("data-index-month"));
    let maxLaptopScreenWidth: MediaQueryList = window.matchMedia("(max-width: 992px)");
    /* events list */
    const eventsListMonths: NodeListOf<HTMLElement> = eventsList.querySelectorAll(".calendar-month");
    const eventsListPrevMonthButton: HTMLButtonElement = eventsList.querySelector(".change-month-button--prev");
    const eventsListNextMonthButton: HTMLButtonElement = eventsList.querySelector(".change-month-button--next");
    /* classnames */
    const activeCalendarClass: string = "active";

    const monthChangeButtons: { button: HTMLButtonElement; directionIndex: number }[] = [
        { button: calendarPrevMonthButton, directionIndex: -1 },
        { button: calendarNextMonthButton, directionIndex: 1 },
        { button: eventsListPrevMonthButton, directionIndex: -1 },
        { button: eventsListNextMonthButton, directionIndex: 1 },
    ];

    /* toggle calendar header + main view */
    const toggleCalendarState = () => {
        calendarMonthsHeader[activeCalendarMonthIndex].classList.toggle(activeCalendarClass);
        calendarMonths[activeCalendarMonthIndex].classList.toggle(activeCalendarClass);
        eventsListMonths[activeCalendarMonthIndex].classList.toggle(activeCalendarClass);
    };

    /* set prev + next buttons disabled state for first & last day in month view */
    const checkPrevNextMonthButtonState = () => {
        monthChangeButtons.forEach(({ button, directionIndex }) => {
            if (directionIndex == 1) {
                button.disabled = activeCalendarMonthIndex === totalMonths - 1;
            } else {
                button.disabled = activeCalendarMonthIndex === 0;
            }
        });
    };

    /* month change functionality (prev/next month button clicked) */
    const handleVisibleMonthChangeButtonClick = (updatedIndex: number) => {
        /* remove old active state */
        toggleCalendarState();

        /* update active index (and check prev/next button disabled status) */
        activeCalendarMonthIndex = updatedIndex;

        checkPrevNextMonthButtonState();

        /* set new active state */
        toggleCalendarState();

        updateActiveDay();
        updateEventsListMonthLabel();
    };

    // reset active day to first month day (or current day if current month is active) - relevant for mobile version
    const updateActiveDay = () => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        if (activeCalendar == calendarTodayView) {
            handleDayChange(curActiveDay, calendarDayTodayView);
        } else {
            const firstDayOfCurrentMonth: HTMLElement = activeCalendar.querySelector(
                ".calendar-day:not(.calendar-day--not-current-month)",
            );
            handleDayChange(curActiveDay, firstDayOfCurrentMonth);
        }
    };

    /* Update the events list prev/next month labels (or remove text for disabled month buttons) */
    const updateEventsListMonthLabel = () => {
        eventsListPrevMonthButton.textContent = calendarMonthsHeader[activeCalendarMonthIndex - 1]?.textContent ?? "";
        eventsListNextMonthButton.textContent = calendarMonthsHeader[activeCalendarMonthIndex + 1]?.textContent ?? "";
    };

    // Check if the top of the section is above the viewport or below it and scroll if not visible
    const scrollMonthContainerInView = (parentSection: HTMLElement) => {
        const container = parentSection.querySelector(".calendar-months");
        const rect = container.getBoundingClientRect();

        if (rect.top < 0 || rect.top > window.innerHeight) {
            window.scrollTo({
                top: parentSection.getBoundingClientRect().top + window.scrollY,
                behavior: "smooth",
            });
        }
    };

    /* Prev/next month nav buttons */
    monthChangeButtons.forEach(({ button, directionIndex }) => {
        button.addEventListener("click", () => {
            handleVisibleMonthChangeButtonClick(activeCalendarMonthIndex + directionIndex);

            // scroll months container into view if top is not visible
            scrollMonthContainerInView(button.closest("section"));
        });
    });

    /* Show current month button */
    calendarTodayButton.addEventListener("click", () => {
        handleVisibleMonthChangeButtonClick(calendarTodayViewIndex);
    });

    /* calendar day change functionality (mobile version) */
    const checkPrevNextDayButtonState = (activeDay: HTMLElement) => {
        if (activeDay) {
            prevDayButton.disabled = activeDay.previousElementSibling == null;
            nextDayButton.disabled = activeDay.nextElementSibling == null;
        }
    };

    /* Set today's day active */
    const setTodayDayActive = (scrollIntoView: boolean = true) => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        handleDayChange(curActiveDay, calendarDayTodayView, scrollIntoView);
    };

    /* Mobile: Change currently active day */
    const handleDayChange = (
        currentActiveChild: HTMLElement,
        newActiveChild: HTMLElement,
        scrollIntoView: boolean = true,
    ) => {
        // remove old active day content
        const curActiveDay = calendarDayMobileWrapper.querySelector(".calendar-day");
        if (curActiveDay) {
            const clonedCalendarDayButtons = curActiveDay.querySelectorAll(".event");
            clonedCalendarDayButtons.forEach((button: HTMLButtonElement) => {
                const newButton = button.cloneNode(true);
                button.parentNode.replaceChild(newButton, button);
            });
            curActiveDay.remove();
        }

        // clone new active day
        const clonedCalendarDay: HTMLElement = newActiveChild.cloneNode(true) as HTMLElement;
        calendarDayMobileWrapper.prepend(clonedCalendarDay);

        // remove mobile button (not needed in this view)
        const mobileButton = clonedCalendarDay.querySelector(".calendar-day-mobile-button");
        if (mobileButton) {
            mobileButton.remove();
        }

        // add overlay open / event listeners to event buttons inside the cloned day element
        const clonedCalendarDayButtons = clonedCalendarDay.querySelectorAll(".event");

        clonedCalendarDayButtons.forEach((button: HTMLButtonElement) => {
            new Dialog(button);
        });

        // change active state inside calendar view
        currentActiveChild.classList.toggle(activeCalendarClass);
        newActiveChild.classList.toggle(activeCalendarClass);

        // scroll calendar day into view if not fully visible
        if (
            scrollIntoView &&
            maxLaptopScreenWidth.matches &&
            calendar.getBoundingClientRect().bottom > window.innerHeight
        ) {
            calendar.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        }

        checkPrevNextDayButtonState(newActiveChild);
    };

    prevDayButton.addEventListener("click", () => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        const prevSibling = curActiveDay.previousElementSibling;
        if (!(prevSibling instanceof HTMLElement)) {
            return;
        }
        handleDayChange(curActiveDay, prevSibling);
    });

    nextDayButton.addEventListener("click", () => {
        const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");
        const nextSibling = curActiveDay.nextElementSibling;
        if (!(nextSibling instanceof HTMLElement)) {
            return;
        }
        handleDayChange(curActiveDay, nextSibling);
    });

    calendarDays.forEach((calendarDay: HTMLElement) => {
        const dayChangeButton: HTMLButtonElement = calendarDay.querySelector(".calendar-day-mobile-button");
        if (!dayChangeButton) {
            return;
        }
        dayChangeButton.addEventListener("click", () => {
            const curActiveDay: HTMLElement = calendar.querySelector(".calendar-day.active");

            handleDayChange(curActiveDay, calendarDay);
        });
    });

    /* Calc single calendar column (used to style multi day events) */
    const calcCalendarColGrid = () => {
        calendar.style.setProperty("--grid-col-width", `${calendarDays[0].clientWidth}px`);
    };

    document.addEventListener("DOMContentLoaded", () => {
        setTimeout(() => {
            calcCalendarColGrid();
        }, 500);
    });

    window.addEventListener("resize", () => {
        calcCalendarColGrid();
    });

    /* add hover for multiDay events that break to the next row in the layout */
    let activeMultiDayEventButtons: NodeListOf<HTMLButtonElement> = null;
    const setMultiDayEventHoverState = (eventDialogId: string, setActive: boolean) => {
        activeMultiDayEventButtons = activeCalendar.querySelectorAll(`.event[data-dialog-id="${eventDialogId}"]`);
        activeMultiDayEventButtons.forEach((activeEventButton) => {
            if (setActive) {
                activeEventButton.classList.add("event--hover");
            } else {
                activeEventButton.classList.remove("event--hover");
            }
        });

        if (!setActive) {
            activeMultiDayEventButtons = null;
        }
    };

    // initial active day
    setTodayDayActive(false);

    /* Event click -> show overlay & check multirow events hover */
    calendarEvents.forEach((calendarEvent: HTMLButtonElement) => {
        // hover class for multiDay events that break to the next row in the layout */
        if (calendarEvent.classList.contains("event--multi-row")) {
            const eventId = calendarEvent.getAttribute("data-dialog-id");

            // hover class for multiDay events that break to the next row in the layout */
            calendarEvent.addEventListener("mouseover", () => {
                setMultiDayEventHoverState(eventId, true);
            });
            calendarEvent.addEventListener("mouseleave", () => {
                setMultiDayEventHoverState(eventId, false);
            });
        }
    });
}
